import React, { useCallback, useEffect } from 'react';
import WidgetGroupProcessor from '@kemu-io/kemu-core/widgets/widgetGroup/index.js';
import { Position, WidgetGroupState , GroupWidgetPort } from '@kemu-io/kemu-core/types';
import { decodePortName } from '@kemu-io/kemu-core/common/utils';
import { RecipeType } from '@kemu-io/kemu-types';
import { useSelector } from 'react-redux';
import useReactiveWidgetState from '../../common/hooks/useReactiveWidgetState';
import { PlumbDragEvent } from '../../types/core_t';
import { decodeDomId } from '../../common/utils';
import VirtualPort from './virtualPort';
import { useLogicMapperContext } from '@common/context/LogicMapperContext/LogicMapperContext';
import { selectSelectedItems, SelectedVirtualPortInfo } from '@src/features/LogicMapper/logicMapperSlice';

interface Props {
	blockId: string;
	recipeId: string;
	recipeType: RecipeType;
	/** id of the current group */
	groupId: string;
}

/**
 * Helper function to update port positions with delta movement
 * @param ports The ports to update
 * @param selectedVirtualPorts The selected virtual ports
 * @param deltaX The delta x
 * @param deltaY The delta y
 * @returns The updated ports
 */
const updatePortPositions = (ports: GroupWidgetPort[], selectedVirtualPorts: string[], deltaX: number, deltaY: number): GroupWidgetPort[] => {
	return ports.map((port) => {
		if (selectedVirtualPorts.includes(port.name)) {
			return {
				...port,
				position: {
					x: (port.position?.x || 0) + deltaX,
					y: (port.position?.y || 0) + deltaY
				}
			};
		}
		return port;
	});
};

/**
 * Utility function to move all selected virtual ports as a group
 * @param currentState Current widget group state
 * @param draggedPortName Name of the port being dragged
 * @param newPosition New position of the dragged port
 * @param selectedVirtualPorts Array of selected port names
 * @returns Updated state with all selected ports moved, or null if invalid
 */
const moveSelectedPorts = (
	currentState: WidgetGroupState,
	draggedPortName: string,
	newPosition: Position,
	selectedVirtualPorts: string[]
): WidgetGroupState | null => {
	// Find the original position of the dragged port to calculate delta
	const originalPort = currentState.outputs.find(p => p.name === draggedPortName) ||
						 currentState.inputs.find(p => p.name === draggedPortName);

	if (!originalPort?.position) {
		return null;
	}

	const deltaX = newPosition.x - originalPort.position.x;
	const deltaY = newPosition.y - originalPort.position.y;

	// Apply the same delta to all selected virtual ports
	return {
		...currentState,
		outputs: updatePortPositions(currentState.outputs, selectedVirtualPorts, deltaX, deltaY),
		inputs: updatePortPositions(currentState.inputs, selectedVirtualPorts, deltaX, deltaY),
	};
};

/**
 * Helper function to build the new state after a port is moved
 * @param groupId The id of the group
 * @param currentState The current state of the group
 * @param evt The event that triggered the move
 * @param selectedVirtualPorts The selected virtual ports
 * @returns The new state of the group
 */
const buildStateAfterMove = (groupId: string, currentState: WidgetGroupState, evt: PlumbDragEvent, selectedVirtualPorts: string[]): WidgetGroupState | null => {
	const widget = decodeDomId(evt.el.id);
	if (!widget?.uid || widget.gateId !== groupId) {
		return null;
	}

	const portName = widget.uid;
	const decodedPortName = decodePortName(portName).name;

	// Calculate the new position
	const newPosition: Position = {
		x: evt.pos.left,
		y: evt.pos.top
	};

	// If this is part of a group selection, move all selected virtual ports
	if (selectedVirtualPorts.length > 1 && selectedVirtualPorts.includes(decodedPortName)) {
		return moveSelectedPorts(currentState, decodedPortName, newPosition, selectedVirtualPorts);
	}

	// Single port movement
	const newState: WidgetGroupState = {
		...currentState,
		outputs: currentState.outputs.map((output) => output.name === decodedPortName ? { ...output, position: newPosition } : output),
		inputs: currentState.inputs.map((input) => input.name === decodedPortName ? { ...input, position: newPosition } : input),
	};

	return newState;
};


const VirtualPortManager = (props: Props): React.JSX.Element | null => {
	const { jsPlumb: plumbInstance } = useLogicMapperContext();
	const [widgetState, setWidgetState] = useReactiveWidgetState<WidgetGroupState>(props.recipeId, props.blockId, props.groupId);
	const selectedItems = useSelector(selectSelectedItems);

	// Get the selected virtual ports for this group
	const selectedVirtualPorts = selectedItems
		.filter((item): item is SelectedVirtualPortInfo => 'portName' in item)
		.filter(item => item.widgetId === props.groupId)
		.map(item => decodePortName(item.portName).name);

	const widgetInputs = WidgetGroupProcessor.getInputNames(widgetState, {
		recipePoolId: props.recipeId,
		recipeType: props.recipeType,
		thingRecipeId: props.blockId,
		widgetId: props.groupId
	});

	const widgetOutputs = WidgetGroupProcessor.getOutputNames(widgetState, {
		recipePoolId: props.recipeId,
		recipeType: props.recipeType,
		thingRecipeId: props.blockId,
		widgetId: props.groupId,
	});

	const filteredOutputs = widgetOutputs.filter(port => port.isInner);
	const filteredInputs = widgetInputs.filter(port => port.isInner);

	const updatePortPosition = useCallback((port: GroupWidgetPort, newPosition: Position): void => {
		setWidgetState(currentState => {
			const portName = decodePortName(port.name).name;
			const newState: WidgetGroupState = {
				...currentState,
				outputs: currentState.outputs.map((input) => input.name === portName ? { ...input, position: newPosition } : input),
				inputs: currentState.inputs.map((output) => output.name === portName ? { ...output, position: newPosition } : output),
			};
			return newState;
		});

	}, [setWidgetState]);


	useEffect(() => {
		const handleEventMove = (evt: PlumbDragEvent) => {
			setWidgetState(currentState => {
				const updatedState = buildStateAfterMove(props.groupId, currentState, evt, selectedVirtualPorts);
				return updatedState || currentState;
			});
		};

		plumbInstance?.bind('drag:stop', handleEventMove);
		return () => {
			plumbInstance?.unbind('drag:stop', handleEventMove);
		};
	}, [plumbInstance, props.groupId, setWidgetState, selectedVirtualPorts/* , repaintConnections */]);


	if (!plumbInstance) { return null; }

	return (
		<>
			{/* These become the inputs (source ports) inside the group */}
			{filteredOutputs.map((outputPort, i) => {
				const decodedPortName = decodePortName(outputPort.name).name;
				const isSelected = selectedVirtualPorts.includes(decodedPortName);
				return (
					<VirtualPort
						updatePortPosition={updatePortPosition}
						key={`${props.groupId}_${outputPort.name}_${i}`}
						recipeId={props.recipeId}
						blockId={props.blockId}
						gateId={props.groupId}
						port={outputPort}
						portLocation={'Right'}
						plumbInstance={plumbInstance}
						isInner={!!outputPort.isInner}
						isSelected={isSelected}
					/>
				);
			})}

			{/* These will become the outputs (targets) inside of the group */}
			{filteredInputs.map((inputPort, i) => {
				const decodedPortName = decodePortName(inputPort.name).name;
				const isSelected = selectedVirtualPorts.includes(decodedPortName);
				return (
					<VirtualPort
						updatePortPosition={updatePortPosition}
						key={`${props.groupId}_${inputPort.name}_${i}`}
						recipeId={props.recipeId}
						blockId={props.blockId}
						gateId={props.groupId}
						port={inputPort}
						portLocation={'Left'}
						plumbInstance={plumbInstance}
						isInner={!!inputPort.isInner}
						isSelected={isSelected}
					/>
				);
			})}
		</>
	);
};

export default VirtualPortManager;
